import { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, Plus, X } from 'lucide-react';
import { cleanText, isCleanText } from '@/utils/sanitize';

export type AssessmentLevel = 'COURSE' | 'MODULE' | 'CONTENT_ITEM';
export type AssessmentType = 'PRE' | 'POST';

interface AddAssessmentModalProps {
  isOpen: boolean;
  /** Context of what is being created — used for the heading + submit payload. */
  level: AssessmentLevel;
  type: AssessmentType;
  /** Parent performs the API call + refresh. Throw to keep the modal open. */
  onSave: (data: { title: string; description?: string }) => Promise<void>;
  onClose: () => void;
}

const LEVEL_LABEL: Record<AssessmentLevel, string> = {
  COURSE: 'Course',
  MODULE: 'Module',
  CONTENT_ITEM: 'Content Item',
};

export default function AddAssessmentModal({ isOpen, level, type, onSave, onClose }: AddAssessmentModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const typeLabel = type === 'PRE' ? 'Pre' : 'Post';
  const heading = `Add ${LEVEL_LABEL[level]} ${typeLabel}-Assessment`;

  useEffect(() => {
    if (isOpen) {
      // Sensible default title, editable.
      setTitle(`${LEVEL_LABEL[level]} ${typeLabel}-Assessment`);
      setDescription('');
      setError('');
      setIsSaving(false);
    }
  }, [isOpen, level, typeLabel]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const cleanedTitle = cleanText(title);
    if (cleanedTitle.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }
    if (!isCleanText(cleanedTitle)) {
      setError('Please use only letters, numbers and standard punctuation');
      return;
    }
    setError('');
    setIsSaving(true);
    try {
      await onSave({
        title: cleanedTitle,
        description: description.trim() ? cleanText(description) : undefined,
      });
    } catch {
      // Parent surfaces the error toast; keep the modal open.
    } finally {
      setIsSaving(false);
    }
  };

  const accent = type === 'PRE' ? 'blue' : 'green';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent === 'blue' ? 'bg-blue-600' : 'bg-green-600'}`}>
              <ClipboardCheck className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">{heading}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => { setError(''); setTitle(e.target.value); }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Assessment title"
              autoFocus
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
              placeholder="What this assessment covers"
            />
          </div>
          <p className="text-xs text-gray-400">
            An empty assessment is created (passing score 70%). Add questions and adjust settings afterwards from its details panel.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`px-5 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 font-medium flex items-center gap-2 ${accent === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Create Assessment</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
